<script setup lang="ts">
/**
 * A value to hand on — a webhook URL, an MCP endpoint, a token — shown
 * read-only with a copy button beside it. The value can be selected by hand
 * too (a click selects all of it), so a refused clipboard never blocks.
 * An optional `label` above and the default slot under it (a hint).
 */
import { useId } from 'vue';
import NxCopyButton from './NxCopyButton.vue';

const props = withDefaults(
  defineProps<{
    value: string;
    label?: string;
    /** Monospace value (URLs, tokens, ids). */
    mono?: boolean;
    copyLabel?: string;
    copiedLabel?: string;
    disabled?: boolean;
  }>(),
  {
    label: undefined,
    mono: true,
    copyLabel: 'Kopieren',
    copiedLabel: 'Kopiert',
    disabled: false,
  }
);

const emit = defineEmits<{
  copied: [value: string];
  error: [];
}>();

const id = useId();

function selectAll(event: Event) {
  (event.target as HTMLInputElement).select();
}
</script>

<template>
  <div class="nx-copy-field" :class="{ 'is-disabled': props.disabled }">
    <label v-if="props.label" class="nx-copy-field__label" :for="id">{{ props.label }}</label>
    <div class="nx-copy-field__row">
      <input
        :id="id"
        class="nx-copy-field__value"
        :class="{ 'is-mono': props.mono }"
        type="text"
        readonly
        :value="props.value"
        :disabled="props.disabled"
        data-testid="nx-copy-field-value"
        @focus="selectAll"
      />
      <NxCopyButton
        :value="props.value"
        :label="props.copyLabel"
        :copied-label="props.copiedLabel"
        :disabled="props.disabled"
        @copied="emit('copied', $event)"
        @error="emit('error')"
      />
    </div>
    <div v-if="$slots.default" class="nx-copy-field__hint">
      <slot />
    </div>
  </div>
</template>

<style scoped>
.nx-copy-field {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  min-width: 0;
}
.nx-copy-field__label {
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
}
.nx-copy-field__row {
  display: flex;
  align-items: stretch;
  gap: var(--nxt-space-xs);
  min-width: 0;
}
.nx-copy-field__value {
  flex: 1 1 auto;
  min-width: 0;
  height: var(--nxt-control-size-sm);
  padding: 0 var(--nxt-space-sm);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-surface-sunken-bg);
  color: var(--nxt-fg);
  font: inherit;
  font-size: var(--nxt-font-size-sm);
  text-overflow: ellipsis;
}
.nx-copy-field__value.is-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: var(--nxt-font-size-xs);
}
.nx-copy-field__value:focus-visible {
  outline: none;
  border-color: var(--nxt-brand-border);
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-copy-field.is-disabled .nx-copy-field__value {
  opacity: 0.6;
}
.nx-copy-field__hint {
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  line-height: var(--nxt-line-height-normal);
}
</style>
